import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, ShieldAlert, TrendingUp, ChevronRight, Shield } from 'lucide-react';
import { usePlanEntitlement } from '../../hooks/usePlanEntitlement';
import { useCheckout } from '../../hooks/useCheckout';
import { PlanOptionCards } from '../Billing/PlanOptionCards';
interface UnlockScreenProps {
  /** Completes onboarding. Any pasted text is carried into the scan. */
  onUnlock: (pastedText?: string) => void;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
export const UnlockScreen: React.FC<UnlockScreenProps> = ({
  onUnlock
}) => {
  const [pastedText, setPastedText] = useState('');
  const [showPlans, setShowPlans] = useState(false);
  const {
    planId,
    accessLabel,
    freeScanAvailable,
    canScan,
    loading
  } = usePlanEntitlement();
  const {
    onCheckout,
    pendingPlan
  } = useCheckout('onboarding');
  const features = [{
    icon: AlertTriangle,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    title: 'Manipulation Patterns',
    description: 'Detects psychological warfare tactics'
  }, {
    icon: ShieldAlert,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    title: 'Gaslighting Detection',
    description: 'Exposes reality distortion attempts'
  }, {
    icon: TrendingUp,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    title: 'Toxicity Score',
    description: 'Calculates relationship danger level'
  }];
  // A user who can still scan starts scanning. Everyone else buys the
  // cheapest full scan — never pushed straight into a subscription.
  const canStartScan = canScan || freeScanAvailable;
  return <motion.div className="w-full max-w-md mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-1361" data-mp="n4n6jbydcoz2">
      <motion.div className="text-center mb-8" variants={itemVariants} data-id="element-1362" data-mp="w3s1676t7y5y">
        <div className="inline-flex items-center gap-2 bg-[#FF0000]/10 text-[#FF0000] px-4 py-1.5 border border-[#FF0000]/20 mb-6 backdrop-blur-sm" data-id="element-1363" data-mp="w1nph32o5uv4">
          <span className="relative flex h-2 w-2" data-id="element-1364" data-mp="15ud05vqeb4pe">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF0000] opacity-75" data-id="element-1365" data-mp="15vo2dr4704ee"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF0000]" data-id="element-1366" data-mp="1frrdhmh0vddh"></span>
          </span>
          <span className="font-mono text-[10px] tracking-widest uppercase font-bold" data-id="element-1367" data-mp="3lkg3c3qx5ibe">
            Forensic Engine Ready
          </span>
        </div>

        <h2 className="text-3xl font-bold mb-3 leading-tight text-white tracking-tight" data-id="element-1368" data-mp="3hkxfaz3swr4c">
          Profile <span className="text-[#FF0000]" data-id="element-1369" data-mp="37fz89niinlzh">Complete</span>.
        </h2>
        <p className="text-sm text-gray-400 leading-relaxed max-w-xs mx-auto font-mono" data-id="element-1370" data-mp="3bfpt7ald37w3">
          Paste a conversation to run your first forensic scan and surface the
          patterns hiding in it.
        </p>
      </motion.div>

      <motion.div className="mb-8 space-y-3" variants={itemVariants} data-id="element-1371" data-mp="32mmmdl581taf">
        {features.map((feature, index) => <motion.div key={index} className={`flex items-center text-left bg-white/5 p-4 border ${feature.border} hover:bg-white/10 transition-colors group relative overflow-hidden`} variants={itemVariants} data-id="element-1372" data-mp="2ntjds39lypvu" data-mpi={index}>
            <div className={`p-2 mr-3 ${feature.bg} border ${feature.border}`} data-id="element-1373" data-mp="2jqebo1tfwq7q">
              <feature.icon size={18} className={feature.color} strokeWidth={2} data-id="element-1374" data-mp="cyvnokz9q457" />
            </div>
            <div className="flex-1" data-id="element-1375" data-mp="hvvtcus7h4vs">
              <h4 className="font-bold text-sm mb-0.5 text-white tracking-wide" data-id="element-1376" data-mp="2e1mrk7udtwmz">
                {feature.title}
              </h4>
              <p className="text-gray-500 text-xs font-mono" data-id="element-1377" data-mp="292tc49vjbvk6">
                {feature.description}
              </p>
            </div>
          </motion.div>)}
      </motion.div>

      {/* Scanner Widget */}
      <motion.div className="relative mb-6 group" variants={itemVariants} data-id="element-1378" data-mp="203ofxa7g4eb6">
        <div className="relative bg-[#0a0a0a] border border-[#FF0000]/30 overflow-hidden group-hover:border-[#FF0000]/50 transition-colors duration-300" data-id="element-1379" data-mp="1v3aewc49i4mn">
          {/* Corner Markers */}
          <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#FF0000] z-20" data-id="element-1380" data-mp="1ldquv34tc4q7" />
          <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#FF0000] z-20" data-id="element-1381" data-mp="1gexi5cxcohr3" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#FF0000] z-20" data-id="element-1382" data-mp="1capu7hypv5tx" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#FF0000] z-20" data-id="element-1383" data-mp="17dg4e17f0s5k" />

          <div className="p-6 text-left relative overflow-hidden" data-id="element-1384" data-mp="3j005vyi1x2ke">
            {/* SCANNING LINE ANIMATION */}
            <motion.div className="absolute top-0 left-0 w-full h-[2px] bg-[#FF0000] shadow-[0_0_20px_#FF0000] z-10" animate={{
            top: ['0%', '100%', '0%']
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-1385" data-mp="3o1h4fnd2ccxt" />

            {/* Grid Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,0,0,0.03)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-1386" data-mp="34910ewciiu51" />

            <div className="relative z-20" data-id="element-1387" data-mp="2glstk9zfrd0w">
              <label htmlFor="onboarding-conversation" className="sr-only" data-id="element-1388" data-mp="2gl1atzqxt9s1">
                Paste your conversation
              </label>
              <textarea id="onboarding-conversation" value={pastedText} onChange={e => setPastedText(e.target.value)} className="w-full h-32 bg-[#050505] border border-gray-800 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white p-4 font-mono text-sm resize-none transition-colors" placeholder="Paste your chat conversation here (e.g., texts, emails, or messages) to begin your forensic analysis..." data-id="element-1389" data-mp="3ov02qricytly" />
              <p className="text-[11px] text-gray-500 font-mono mt-3" data-id="element-1390" data-mp="30z5tm4veqeui">
                Copy the messages from your chat app and paste them here.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Current scan access */}
      <motion.div variants={itemVariants} className="mb-5 flex items-center gap-3 border border-gray-800 bg-[#0a0a0a] p-4" data-id="element-1391" data-mp="u74oq4n8gfs3">
        <Shield size={18} className="shrink-0 text-gray-500" data-id="element-1392" data-mp="p7s2nm46rcqk" />
        <div className="min-w-0" data-id="element-1393" data-mp="xospwpu5goda">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-1394" data-mp="xnr35o3rudfq">
            Current access
          </p>
          <p className="mt-1 font-mono text-sm text-white" data-id="element-1395" data-mp="nr4jv3r3fuft">
            {loading ? 'Verifying clearance…' : freeScanAvailable ? '1 free scan available' : accessLabel}
          </p>
        </div>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-1396" data-mp="1gglhcftkm1iw">
        {canStartScan ? <button type="button" onClick={() => onUnlock(pastedText.trim() || undefined)} className="w-full bg-[#FF0000] py-4 font-bold uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1397" data-mp="1ldle204j5rxc">
            Start free scan
          </button> : <button type="button" onClick={() => void onCheckout('single_scan')} disabled={pendingPlan !== null} className="w-full bg-[#FF0000] py-4 font-bold uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1398" data-mp="247h4gzn308jt">
            {pendingPlan === 'single_scan' ? 'Processing…' : 'Buy a single scan — $2.99'}
          </button>}

        <button type="button" onClick={() => setShowPlans(open => !open)} aria-expanded={showPlans} className="mt-3 w-full border border-gray-700 bg-transparent py-4 font-bold uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1399" data-mp="i13b1b72p513">
          {showPlans ? 'Hide plans' : 'View plans'}
        </button>

        <AnimatePresence initial={false} data-id="element-1400" data-mp="198bk9k8luqgs">
          {showPlans && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-1401" data-mp="1eaaoewc65res">
              <div className="pt-4" data-id="element-1402" data-mp="220eqv0dmfl47">
                <PlanOptionCards onCheckout={plan => void onCheckout(plan)} pendingPlan={pendingPlan} currentPlanId={planId} onStartFree={canStartScan ? () => onUnlock(pastedText.trim() || undefined) : undefined} data-id="element-1403" data-mp="21wzcy0vooi4d" />
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Skip to Dashboard */}
        <motion.button onClick={() => onUnlock()} className="w-full mt-4 text-gray-500 text-xs font-mono uppercase tracking-widest hover:text-white transition-colors duration-200 flex items-center justify-center gap-2 group py-3" whileHover={{
        scale: 1.01
      }} data-id="element-1404" data-mp="10y8eil3vaadz">
          Skip to Dashboard
          <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" data-id="element-1405" data-mp="1ju7yp9qls9n0" />
        </motion.button>
      </motion.div>

      <motion.div className="mt-2 text-center" variants={itemVariants} data-id="element-1406" data-mp="1tkt53jciqigh">
        <p className="text-[10px] text-gray-600 font-mono uppercase tracking-widest" data-id="element-1407" data-mp="1tmx7fwurr9en">
          🔒 Secure Payment • Encrypted Session
        </p>
      </motion.div>
    </motion.div>;
};